import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { cropService } from '../../services/cropService';
import {
	Plus,
	Search,
	Edit,
	Trash2,
	Sprout,
	FolderTree,
	Leaf,
	Filter
} from 'lucide-react';

const CropManagement = () => {
	const [activeTab, setActiveTab] = useState('crop-group');
	const [showAddModal, setShowAddModal] = useState(false);
	const [editingItem, setEditingItem] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [filterGroup, setFilterGroup] = useState('all');
	const [filterStatus, setFilterStatus] = useState('all');
	const [isLoading, setIsLoading] = useState(false);
	const [initialized, setInitialized] = useState(false);

	// Data for crop groups
	const [cropGroups, setCropGroups] = useState([]);

	// Data for crops
	const [crops, setCrops] = useState([]);

	const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

	// API Integration Functions
	const loadCropGroups = async () => {
		try {
			setIsLoading(true);
			const response = await cropService.getCropGroups();
			const data = response.data || response;
			// Sort by ID descending to show newest items first
			const sortedData = Array.isArray(data) ? data.sort((a, b) => (b.id || 0) - (a.id || 0)) : data;
			setCropGroups(sortedData);
		} catch (error) {
			console.warn('Failed to load crop groups from API:', error.message);
			// Do not fallback to hardcoded data
			setCropGroups([]);
		} finally {
			setIsLoading(false);
		}
	};

	const loadCrops = async () => {
		try {
			setIsLoading(true);
			const response = await cropService.getCrops();
			let cropsData = response.data || response;
			
			// API already provides group_name and group_id, no mapping needed
			setCrops(cropsData);
		} catch (error) {
			console.warn('Failed to load crops from API:', error.message);
			// Do not fallback to hardcoded data
			setCrops([]);
		} finally {
			setIsLoading(false);
		}
	};

	// Load data on component mount
	useEffect(() => {
		const loadData = async () => {
			try {
				await loadCropGroups();
				await loadCrops();
			} finally {
				setInitialized(true);
			}
		};
		loadData();
	}, []);

	// Auto-generate sequential crop group code with CRG prefix
	const generateCropGroupCode = () => {
		if (!cropGroups || cropGroups.length === 0) return 'CRG001';
		const maxCode = Math.max(...cropGroups.map(group => parseInt(group?.code?.replace('CRG', '') || '0')), 0);
		const nextNumber = maxCode + 1;
		return `CRG${nextNumber.toString().padStart(3, '0')}`;
	};

	// Auto-generate sequential crop code with CRP prefix
	const generateCropCode = () => {
		const numericValues = crops
			.map(c => (c.code && /\d+/.test(c.code) ? parseInt(c.code.match(/\d+/)[0], 10) : 0));
		const maxNum = numericValues.length ? Math.max(...numericValues) : 0;
		const next = String(maxNum + 1).padStart(3, '0');
		return `CRP${next}`;
	};

	const tabs = [
		{ id: 'crop-group', name: 'Crop Group', icon: FolderTree, data: cropGroups },
		{ id: 'crop', name: 'Crop', icon: Sprout, data: crops }
	];

	const currentData = tabs.find(tab => tab.id === activeTab)?.data || [];
	const filteredData = currentData.filter(item => {
		const q = (searchTerm || '').toLowerCase();
		const haystack = (
			activeTab === 'crop-group'
				? [item.code, item.name, item.status]
				: [item.name, item.group_name, item.status]
		).filter(Boolean).join(' ').toLowerCase();
		const matchesSearch = !q || haystack.includes(q);
		const matchesGroup = filterGroup === 'all' || item.group_name === filterGroup;
		const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
		return matchesSearch && matchesGroup && matchesStatus;
	});

	const handleAdd = async (data) => {
		setIsLoading(true);
		try {
			let response;
			if (activeTab === 'crop-group') {
				const cropGroupData = {
					...data,
					code: generateCropGroupCode() // Auto-generate code
				};
				response = await cropService.createCropGroup(cropGroupData);
				if (response.success) {
					await loadCropGroups(); // Reload data from API
					toast.success('Crop Group added successfully!');
				} else {
					throw new Error(response.message || 'Failed to add crop group');
				}
			} else {
				// For crops, add group_name based on selected groupId
				const selectedGroup = cropGroups.find(group => group.id === parseInt(data.groupId));
				const cropData = {
					...data,
					group_id: parseInt(data.groupId),
					group_name: selectedGroup?.name || '',
					code: data.code || generateCropCode()
				};
				response = await cropService.createCrop(cropData);
				if (response.success) {
					await loadCrops(); // Reload data from API
					toast.success('Crop added successfully!');
				} else {
					throw new Error(response.message || 'Failed to add crop');
				}
			}
			setShowAddModal(false);
			reset();
		} catch (error) {
			console.error('API error:', error);
			toast.error(error.message || 'Failed to add item');
		} finally {
			setIsLoading(false);
		}
	};

	const handleEdit = async (data) => {
		setIsLoading(true);
		try {
			let response;
			if (activeTab === 'crop-group') {
				const cropGroupData = {
					...data,
					code: editingItem.code // Keep existing code, don't allow editing
				};
				response = await cropService.updateCropGroup(editingItem.id, cropGroupData);
				if (response.success) {
					await loadCropGroups(); // Reload data from API
					toast.success('Crop Group updated successfully!');
				} else {
					throw new Error(response.message || 'Failed to update crop group');
				}
			} else {
				// For crops, update group_name based on selected groupId
				const selectedGroup = cropGroups.find(group => group.id === parseInt(data.groupId));
				const cropData = {
					...data,
					group_id: parseInt(data.groupId),
					group_name: selectedGroup?.name || ''
				};
				response = await cropService.updateCrop(editingItem.id, cropData);
				if (response.success) {
					await loadCrops(); // Reload data from API
					toast.success('Crop updated successfully!');
				} else {
					throw new Error(response.message || 'Failed to update crop');
				}
			}
			setShowAddModal(false);
			setEditingItem(null);
			reset();
		} catch (error) {
			console.error('API error:', error);
			toast.error(error.message || 'Failed to update item');
		} finally {
			setIsLoading(false);
		}
	};

	const handleDelete = async (itemId) => {
		if (!window.confirm('Are you sure you want to delete this item?')) return;

		setIsLoading(true);
		try {
			let response;
			if (activeTab === 'crop-group') {
				response = await cropService.deleteCropGroup(itemId);
				if (response.success) {
					await loadCropGroups(); // Reload data from API
					toast.success('Crop Group deleted successfully!');
				} else {
					throw new Error(response.message || 'Failed to delete crop group');
				}
			} else {
				response = await cropService.deleteCrop(itemId);
				if (response.success) {
					await loadCrops(); // Reload data from API
					toast.success('Crop deleted successfully!');
				} else {
					throw new Error(response.message || 'Failed to delete crop');
				}
			}
		} catch (error) {
			console.error('API error:', error);
			toast.error(error.message || 'Failed to delete item');
		} finally {
			setIsLoading(false);
		}
	};

	const openEditModal = (item) => {
		setEditingItem(item);
		setValue('name', item.name);
		if (activeTab === 'crop-group') {
			setValue('code', item.code);
		} else {
			setValue('code', item.code);
		}
		if (activeTab === 'crop') {
			setValue('groupId', item.group_id.toString());
		}
		setValue('status', item.status || 'active');
		setShowAddModal(true);
	};

	const getFormFields = () => {
		if (activeTab === 'crop-group') {
			return (
				<>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
						<input
							type="text"
							{...register('code', { required: 'Code is required' })}
							className="input-field bg-gray-100 cursor-not-allowed font-mono"
							placeholder={editingItem ? "Code cannot be edited" : "Code will be generated automatically"}
							readOnly
						/>
						{errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
						<input
							type="text"
							{...register('name', { required: 'Group name is required' })}
							className="input-field"
							placeholder="e.g., Cereals, Pulses"
						/>
						{errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
						<select
							defaultValue="active"
							{...register('status', { required: 'Status is required' })}
							className="input-field"
						>
							<option value="active">Active</option>
							<option value="inactive">Inactive</option>
						</select>
						{errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
					</div>
				</>
			);
		} else {
			return (
				<>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Crop Group</label>
						<select
							{...register('groupId', { required: 'Crop group is required' })}
							className="input-field"
						>
							<option value="">Select Crop Group</option>
							{cropGroups.map(group => (
								<option key={group.id} value={group.id}>{group.name}</option>
							))}
						</select>
						{errors.groupId && <p className="text-red-500 text-sm mt-1">{errors.groupId.message}</p>}
					</div>
					{!editingItem && (
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
							<input
								type="text"
								value={generateCropCode()}
								className="input-field bg-gray-50 font-mono"
								readOnly
							/>
							<p className="text-xs text-gray-500 mt-1">Auto-generated</p>
						</div>
					)}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Crop Name</label>
						<input
							type="text"
							{...register('name', { required: 'Crop name is required' })}
							className="input-field"
							placeholder="e.g., Rice, Maize"
						/>
						{errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
						<select
							defaultValue="active"
							{...register('status', { required: 'Status is required' })}
							className="input-field"
						>
							<option value="active">Active</option>
							<option value="inactive">Inactive</option>
						</select>
						{errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
					</div>
				</>
			);
		}
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Crops</h1>
					<p className="text-gray-600">Manage crop groups and individual crops</p>
				</div>
				<button 
					onClick={() => {
						setEditingItem(null);
						reset();
						// Pre-fill with auto-generated code for crop groups
						if (activeTab === 'crop-group') {
							setValue('code', generateCropGroupCode());
						}
						setShowAddModal(true);
					}}
					className="btn-primary flex items-center mt-4 sm:mt-0"
				>
					<Plus className="w-5 h-5 mr-2" />
					Add {activeTab === 'crop-group' ? 'Crop Group' : 'Crop'}
				</button>
			</div>

			{/* Tabs */}
			<div className="border-b border-gray-200">
				<nav className="-mb-px flex space-x-8">
					{tabs.map((tab) => {
						const Icon = tab.icon;
						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
									activeTab === tab.id
										? 'border-primary-500 text-primary-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
								}`}
							>
								<Icon className="w-4 h-4 mr-2" />
								{tab.name}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Search and Filter */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
				<div className="relative flex-1 max-w-md">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
					<input
						type="text"
						placeholder={`Search ${activeTab === 'crop-group' ? 'crop group' : 'crop'}...`}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					/>
				</div>
				<div className="flex items-center space-x-2">
					<Filter className="w-4 h-4 text-gray-400" />
					<select
						value={filterStatus}
						onChange={(e) => setFilterStatus(e.target.value)}
						className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
						<option value="all">All Status</option>
						<option value="active">Active</option>
						<option value="inactive">Inactive</option>
					</select>
					{activeTab === 'crop' && (
						<select
							value={filterGroup}
							onChange={(e) => setFilterGroup(e.target.value)}
							className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						>
							<option value="all">All Groups</option>
							{cropGroups.map(group => (
								<option key={group.id} value={group.name}>{group.name}</option>
							))}
						</select>
					)}
				</div>
			</div>

			{/* Data Table */}
			<div className="card overflow-hidden">
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								{activeTab === 'crop-group' ? (
									<>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Code
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Group Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions
										</th>
									</>
								) : (
									<>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Code
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Crop Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Crop Group
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Add/Edit Crop
										</th>
									</>
								)}
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{filteredData.map((item) => (
								<tr key={item.id} className="hover:bg-gray-50">
									{activeTab === 'crop-group' ? (
										<>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-mono font-semibold text-blue-600">{item.code}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-medium text-gray-900">{item.name}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className={`px-2 py-1 text-xs font-medium rounded-full ${
													item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
												}`}
												>
													{item.status}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
												<div className="flex space-x-2">
													<button
														onClick={() => openEditModal(item)}
														className="text-blue-600 hover:text-blue-800"
													>
														<Edit className="w-4 h-4" />
													</button>
													<button
														onClick={() => handleDelete(item.id)}
														className="text-red-600 hover:text-red-800"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</td>
										</>
									) : (
										<>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-mono font-semibold text-blue-600">{item.code}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-medium text-gray-900">{item.name}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
													{item.group_name}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className={`px-2 py-1 text-xs font-medium rounded-full ${
													item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
												}`}
												>
													{item.status}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
												<div className="flex space-x-2">
													<button
														onClick={() => openEditModal(item)}
														className="text-blue-600 hover:text-blue-800"
													>
														<Edit className="w-4 h-4" />
													</button>
													<button
														onClick={() => handleDelete(item.id)}
														className="text-red-600 hover:text-red-800"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</td>
										</>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				
				{filteredData.length === 0 && (
					<div className="text-center py-12">
						<Sprout className="w-12 h-12 text-gray-400 mx-auto mb-4" />
						<p className="text-gray-500">No {activeTab === 'crop-group' ? 'crop group' : 'crop'} found</p>
					</div>
				)}
			</div>

			{/* Pagination */}
			<div className="flex items-center justify-between mt-4">
				<div className="text-sm text-gray-700">
					Showing 1 to {filteredData.length} of {filteredData.length} results
				</div>
				<div className="flex items-center space-x-2">
					<button
						disabled
						className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
					>
						Previous
					</button>
					<span className="px-3 py-1 text-sm text-gray-700">
						Page 1 of 1
					</span>
					<button
						disabled
						className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
					>
						Next
					</button>
				</div>
			</div>

			{/* Add/Edit Modal */}
			{showAddModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{editingItem ? 'Edit' : 'Add'} {activeTab === 'crop-group' ? 'Crop Group' : 'Crop'}
							</h2>
							<form onSubmit={handleSubmit(editingItem ? handleEdit : handleAdd)} className="space-y-4">
								{getFormFields()}
								<div className="flex justify-end space-x-3 pt-4">
									<button
										type="button"
										onClick={() => {
											setShowAddModal(false);
											setEditingItem(null);
											reset();
										}}
										className="btn-secondary"
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={isLoading}
										className="btn-primary"
									>
										{isLoading ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CropManagement; 